/**
 * What happened in a room, after everyone has gone home.
 *
 * Read-only and available on every tier — "free can see, paid can download" is the line the product
 * draws, so this page never gates the reading. Entitlement comes from the server's `canExport`; the
 * client never derives it from the plan name, or the tier table would live in two places and drift.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, barTotal, labelOf, revealRows, type QuestionAggregate, type RoundResult, type SessionResults } from "../api.js";
import { getLocale, t } from "../i18n.js";
import { Bar, MeanNote } from "../components.js";

type Load =
  | { state: "loading" }
  | { state: "error"; code: "notFound" | "unavailable" }
  | { state: "ready"; results: SessionResults };

export function Results({ sessionId }: { sessionId: string }): React.JSX.Element {
  const [load, setLoad] = useState<Load>({ state: "loading" });

  useEffect(() => {
    let cancelled = false;
    void api.results(sessionId).then(({ status, data }) => {
      if (cancelled) return;
      if (data) setLoad({ state: "ready", results: data });
      // 404 is also what a foreign tenant gets, deliberately — the page says the same thing either
      // way, because "not yours" and "does not exist" must be indistinguishable from out here.
      else setLoad({ state: "error", code: status === 404 ? "notFound" : "unavailable" });
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (load.state === "loading") return <Shell>{t("results.loading")}</Shell>;
  if (load.state === "error") {
    return <Shell>{t(load.code === "notFound" ? "results.notFound" : "results.unavailable")}</Shell>;
  }

  const { results } = load;
  return (
    <Shell>
      <header className="mb-8 flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="font-display text-display-md">{t("results.title")}</h1>
        <Link to="/host" className="no-print text-sm underline">
          {t("results.back")}
        </Link>
      </header>

      {/* Paper only. A printed page that does not say what it is or when it ran is a screenshot,
          not a report — and this is the artifact someone forwards to a client. */}
      <p className="print-only mb-6 text-sm">
        {t("results.reportHeader", {
          id: results.sessionId,
          date: new Date().toLocaleString(),
        })}
      </p>

      <Summary results={results} />
      <ExportControl canExport={results.canExport} sessionId={results.sessionId} />

      {results.withheldByRetention > 0 && (
        <p className="mt-6 rounded-lg border border-muted p-4 text-sm">
          {t("results.withheld", {
            n: String(results.withheldByRetention),
            days: String(results.retentionDays),
          })}{" "}
          <span className="opacity-70">{t("results.withheldHint")}</span>
        </p>
      )}

      {results.rounds.length === 0 ? (
        <p className="mt-8 text-sm opacity-80">{t("results.empty")}</p>
      ) : (
        <ol className="mt-8 space-y-6">
          {results.rounds.map((round) => (
            <Round key={round.ord} round={round} />
          ))}
        </ol>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <main className="mx-auto max-w-3xl px-4 py-10">{children}</main>;
}

function Summary({ results }: { results: SessionResults }): React.JSX.Element {
  const { summary } = results;
  return (
    <dl className="grid grid-cols-3 gap-4">
      <Stat value={summary.rounds} label={t("results.rounds")} />
      <Stat value={summary.totalResponses} label={t("results.totalResponses")} />
      {/* Labelled "busiest round", never "participants". Nobody counts the room, and a number
          that looks like attendance but is not would be quoted in a report as if it were. */}
      <Stat value={summary.peakResponses} label={t("results.peakResponses")} hint={t("results.peakHint")} />
    </dl>
  );
}

function Stat({ value, label, hint }: { value: number; label: string; hint?: string }): React.JSX.Element {
  return (
    <div className="rounded-lg border border-muted p-4">
      <dt className="text-xs uppercase tracking-wide opacity-70">{label}</dt>
      <dd className="font-display text-display-sm">{value}</dd>
      {hint && <p className="mt-1 text-xs opacity-60">{hint}</p>}
    </div>
  );
}

/**
 * The export button, or the reason there isn't one.
 *
 * The locked copy says the tier was frozen at CREATION, because a host who upgrades and comes back
 * to a still-locked button deserves the actual reason rather than believing it is a bug.
 */
function ExportControl({ canExport, sessionId }: { canExport: boolean; sessionId: string }): React.JSX.Element {
  if (!canExport) {
    return (
      <p className="no-print mt-6 rounded-lg border border-muted p-4 text-sm">
        <span className="font-semibold">{t("results.exportLocked")}</span>{" "}
        <span className="opacity-70">{t("results.exportLockedHint")}</span>
      </p>
    );
  }
  return (
    <div className="no-print mt-6 flex flex-wrap gap-3">
      {/* A plain anchor, not a fetch: the browser follows it, so Content-Disposition puts the file
          in Downloads rather than a blob in memory. The server re-checks the plan regardless —
          this button is a convenience, never the gate. */}
      <a
        className="inline-block rounded-md bg-accent px-4 py-2 font-bold text-surface"
        href={api.exportUrl(sessionId, "csv")}
        download
      >
        {t("results.export")}
      </a>
      {/* The report as a FILE: built on the server from the same rows as the CSV, so the plan gate and
          the small-room author floor apply to it exactly as they do there. */}
      <a
        className="inline-block rounded-md bg-accent px-4 py-2 font-bold text-surface"
        href={api.exportUrl(sessionId, "pdf", getLocale())}
        download
      >
        {t("results.exportPdf")}
      </a>
      {/*
        The "print" half of "download as report or CSV".
        `window.print()`, not a bundled client PDF library: jsPDF would ship ~300 kB to every visitor.
        The downloadable PDF above is generated by the Worker (US-LV-8b); this is the on-screen view.
        HONEST LIMIT: this button is a PRODUCT line, not a security control. Ctrl+P exists, and no
        client-side gate survives it. What is actually enforced is the CSV/JSON export, which is
        checked server-side and carries the k-anonymity floor. Do not "harden" this into something
        it can never be.
      */}
      <button
        type="button"
        onClick={() => window.print()}
        className="inline-block rounded-md border border-muted px-4 py-2 font-bold"
      >
        {t("results.report")}
      </button>
    </div>
  );
}

function Round({ round }: { round: RoundResult }): React.JSX.Element {
  const n = round.responses;
  return (
    <li className="rounded-lg border border-muted p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-display-xs">
          <span className="opacity-60">
            {t("results.round")} {round.ord + 1}
          </span>{" "}
          {round.prompt}
        </h2>
        <p className="text-xs opacity-70">
          {n} {t(n === 1 ? "results.responseOne" : "results.responses")}
          {round.scored && <> · {t("results.scored")}</>}
        </p>
      </div>
      <Aggregate agg={round.aggregate} />
    </li>
  );
}

/**
 * Renders whichever arm of the union the round actually stored.
 *
 * The `authors: "hidden"` arm has no `nickname` field to render, so an anonymous round cannot be
 * accidentally de-anonymised here — the same property that keeps it anonymous in storage. Handled
 * through `revealRows`, the shared helper, rather than a second narrowing that could diverge.
 */
function Aggregate({ agg }: { agg: QuestionAggregate }): React.JSX.Element {
  if (agg.type === "mc") {
    // A round that withheld its tally live still recorded the real counts — withholding was a
    // projection concern, and the round is over.
    if (agg.tally === "hidden") return <p className="text-sm opacity-70">{agg.total}</p>;
    return (
      <>
        {/* The mean the room read on the wall, from the SAME numbers, a day later (W9b AC4).
            OUTSIDE the list: a `<p>` is not a list item, and an empty `<li>` on every other round
            is a blank row a screen reader still announces. */}
        <MeanNote agg={agg} className="text-sm opacity-70" />
        {/* A single-screen round is ONE tap by the host for the room (US-LV-22): marked, so the
            `1` in the bar is never read as a participant. */}
        {agg.hosted === true && <p className="text-sm font-bold opacity-70">{t("hosted.badge")}</p>}
        <ul className="space-y-2">
          {/* The unit rides INSIDE the persisted aggregate (AC5), so a result read the next day still
              says whether those numbers are votes or points. Nothing new in D1. */}
          {agg.payload !== undefined && <li className="text-sm opacity-70">{t("prio.unit")}</li>}
          {Object.entries(agg.counts).map(([optionId, count]) => (
            <li key={optionId}>
              {/* The same Bar the live projection uses, so a correct answer is marked the same way
                  here — including its sr-only label — instead of a second renderer to keep in step.
                  The LABEL comes off the persisted option table; the id is what a round closed
                  before W9b has on file, and printing that is better than printing nothing. */}
              <Bar
                label={labelOf(agg.options, optionId) ?? optionId}
                value={count}
                total={barTotal(agg)}
                highlight={agg.correctOptionIds?.includes(optionId) ?? false}
              />
            </li>
          ))}
        </ul>
      </>
    );
  }

  if (agg.type === "wordcloud") {
    return (
      <ul className="flex flex-wrap gap-2">
        {agg.words.map((w) => (
          <li key={w.word} className="rounded bg-muted px-2 py-1 text-sm">
            {w.word} <span className="opacity-60">×{w.count}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (agg.type === "open") {
    return (
      <ul className="space-y-2">
        {agg.entries.map((e) => (
          <li key={e.id} className="text-sm">
            {e.text} <span className="opacity-60">▲{e.upvotes}</span>
          </li>
        ))}
      </ul>
    );
  }

  // promptvote. Only a reveal is ever persisted (a write/vote phase is not a terminal close), but
  // the other arms are handled rather than cast away.
  if (agg.phase !== "reveal") return <p className="text-sm opacity-70">—</p>;
  return (
    <>
      {agg.authors === "hidden" && <p className="mb-2 text-xs opacity-60">{t("results.anonymous")}</p>}
      <ol className="space-y-2">
        {revealRows(agg).map((r) => (
          <li key={r.id} className="text-sm">
            {r.text}
            {r.author && <span className="opacity-60"> — {r.author}</span>}{" "}
            <span className="opacity-60">
              {r.votes} {t("results.votes")}
            </span>
          </li>
        ))}
      </ol>
    </>
  );
}

export function ResultsRoute(): React.JSX.Element {
  const { id } = useParams();
  return <Results sessionId={id ?? ""} />;
}
